// ---------------------------------------------------------------------------
// Shared: Condition Evaluator
//
// Used by both the renderer (conditional coloring) and crystallizer
// (data-driven conditional resolution).
// ---------------------------------------------------------------------------

/**
 * Evaluate a condition against a data object.
 *
 * Supports operators: eq, neq, gt, lt, gte, lte, in.
 * Field paths use dot notation (e.g. "net_profit_yoy" or "branch.field").
 *
 * @param {object|null} condition — { field, op, value }
 * @param {object} data
 * @returns {boolean}
 */
export function evaluateCondition(condition, data) {
  if (!condition) return true;
  const { field, op, value } = condition;
  const dataValue = field.split(".").reduce((obj, key) => (obj != null ? obj[key] : undefined), data);

  switch (op) {
    case "eq": return dataValue === value;
    case "neq": return dataValue !== value;
    case "gt": return Number(dataValue) > Number(value);
    case "lt": return Number(dataValue) < Number(value);
    case "gte": return Number(dataValue) >= Number(value);
    case "lte": return Number(dataValue) <= Number(value);
    case "in": return Array.isArray(value) && value.includes(dataValue);
    default: return false;
  }
}

/**
 * Evaluate a conditional color spec against crystallized data.
 *
 * A conditional color spec looks like:
 *   { op: "cond", branches: [{ when: { field, op, value }, then: "#color" }], default: "#color" }
 *
 * Returns the resolved color string, or the default, or "#999".
 *
 * @param {object|string|null} colorSpec
 * @param {object} crystalizedData
 * @returns {string}
 */
export function evaluateConditionalColor(colorSpec, crystalizedData) {
  if (!colorSpec || typeof colorSpec !== "object" || colorSpec.op !== "cond") {
    return colorSpec;
  }
  for (const branch of (colorSpec.branches || [])) {
    if (evaluateCondition(branch.when, crystalizedData)) {
      return branch.then;
    }
  }
  return colorSpec.default || "#999";
}
